import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Close, Refresh } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

const DatahubTrackLogModal = ({ open, onClose, flowData, isFlowRunning }) => {
  const [trackLogs, setTrackLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);

  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId');

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      if (dateString.includes('T') && dateString.includes('Z')) {
        return new Date(dateString).toLocaleString();
      }
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  const fetchTrackLogs = useCallback(async (showLoading = true) => {
    if (!flowData || !flowData.instanceId) return;

    if (showLoading) setLoading(true);
    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/datahub-track-log/flow-instance/${flowData.instanceId}${tenantId ? `?tenantId=${tenantId}` : ''}`;
      
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Map the data for display
      const mappedData = data.map((log, index) => ({
        id: log.id || index,
        application: log.application || 'N/A',
        category: log.category || 'N/A',
        subCategory: log.subCategory || 'N/A',
        targetTable: log.targetTable || 'N/A',
        sourceRecordId: log.sourceRecordId || 'N/A',
        description: log.description || 'N/A',
        status: log.status || 'N/A',
        dateStarted: log.dateStarted || null,
        dateCompleted: log.dateCompleted || null,
        message: log.message || log.errorMessage || 'N/A',
        errorMessage: log.errorMessage || null,
        errorType: log.errorType || null,
        recordsProcessed: log.recordsProcessed || 0,
      }));

      setTrackLogs(mappedData);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching track logs:', error);
      if (showLoading) {
        toast.error(`Error loading track logs: ${error.message}`);
      }
      setTrackLogs([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [flowData, token, tenantId]);

  // Set up polling when modal opens or flow starts running
  useEffect(() => {
    if (open && flowData) {
      // Initial fetch
      fetchTrackLogs(true);

      // Set up polling - always poll when modal is open to see records appear in real-time
      // Poll every 2 seconds to catch new records as they are processed
      const interval = setInterval(() => {
        fetchTrackLogs(false); // Don't show loading spinner for background updates
      }, 2000); // Poll every 2 seconds

      setPollInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, [open, flowData, fetchTrackLogs]);

  const handleManualRefresh = () => {
    fetchTrackLogs(true);
  };

  const handleClose = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'system_error':
        return 'error';
      case 'started':
        return 'info';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'targetTable',
      headerName: 'Table Name',
      width: 200,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'sourceRecordId',
      headerName: 'Source Record ID',
      width: 200,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dateStarted',
      headerName: 'Date',
      width: 200,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value || 'N/A'}
            color={getStatusColor(params.value)}
            size="small"
            variant={params.value === 'started' ? 'filled' : 'outlined'}
          />
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 300,
      headerClassName: 'grid-header',
      renderCell: (params) => {
        const status = params.row.status?.toLowerCase();
        const isFailed = status?.includes('failed') || status?.includes('error');
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" style={{ 
              color: isFailed ? '#d32f2f' : 'inherit',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              fontWeight: isFailed ? 500 : 'normal'
            }}>
              {params.value || 'N/A'}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Datahub Track Log</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {lastRefresh && (
              <Typography variant="caption" color="text.secondary">
                Last refreshed: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
            <IconButton
              onClick={handleManualRefresh}
              disabled={loading}
              size="small"
              title="Refresh"
            >
              <Refresh />
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
        {flowData && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Flow: {flowData.sfl_description || flowData.instanceId}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataGrid
              rows={trackLogs}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              autoHeight={false}
              sx={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                '& .grid-header': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  verticalAlign: 'middle',
                },
                '& .MuiDataGrid-row': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {isFlowRunning 
                        ? "Hub Flow is running... Logs will appear here automatically"
                        : "No track logs found"}
                    </Typography>
                    {isFlowRunning && <CircularProgress size={24} />}
                  </Box>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DatahubTrackLogModal;

